/**
 * Helpers for rendering and validating tenant-defined custom fields.
 *
 * The server (CustomFieldService, P1) is the source of truth — these utilities
 * mirror its rules for immediate client-side feedback only. See
 * docs/platform/custom-fields-design.md.
 */
import type { TFunction } from 'i18next';

import i18n from '../../../i18n/config';
import {
  resolveRelationshipClassLabel,
  type RelationshipClass,
} from './useRelationshipClasses';
import type { CustomFieldDefinition } from '../../../generated/api/models/customFieldDefinition';
// Re-exported, never re-typed: the route now carries a response_model, so orval
// generates this shape and a hand-written twin would be free to drift from it.
export type { RelationshipClassCarrierCount } from '../../../generated/api/models/relationshipClassCarrierCount';
import type { RelationshipClassCarrierCount } from '../../../generated/api/models/relationshipClassCarrierCount';
import type { CustomFieldOption } from '../../../generated/api/models/customFieldOption';

/**
 * Query key for the tenant's whole definition set, across every record type.
 *
 * It lives in this pure module rather than beside its hook because TWO hooks in
 * two files must name the identical key — that shared identity is the only thing
 * making the cross-record-type count free for the second caller on a page — and
 * sixteen specs mock the hook's module by listing its exports, so importing the
 * key from there breaks all of them the moment a shared component needs it.
 * `useCustomFieldDefinitions` re-exports it for its existing consumers.
 */
export const allCustomFieldDefinitionsKey = ['custom-fields', 'definitions', 'all'] as const;

/** Per-field validation messages, keyed by the definition's `key`. */
export type CustomFieldErrors = Record<string, string>;

/** Everything that depends on an entity carrying relationship classes. */
export interface RelationshipScopedEntity {
  /** `common:businessClassification.*` group holding its built-in relationship labels. */
  labelGroup: 'companyRelationship' | 'contactRelationship';
  /**
   * The OTHER relationship-bearing record type — the plausible wrong answer to
   * "why isn't my field here?", since a relationship is tenant-GLOBAL while a
   * definition belongs to exactly one `entity_type`.
   */
  sibling: string;
  /** i18n key for "N more fields for these relationships are defined on <sibling>". */
  otherCountKey: string;
  /**
   * i18n key for "no records of THIS type carry <class>, but N of the sibling
   * type do" — the definition dialog's zero-carrier warning. It names the
   * sibling's tenant-customizable noun, so it has to live in the string.
   */
  zeroCarrierElsewhereKey: string;
}

/**
 * Custom-field-enabled entities that carry relationship classes (Phase 2).
 *
 * ONE registry, deliberately: three facts about the same closed key set used to
 * live in three hand-maintained maps, and adding a third relationship-bearing
 * entity to only two of them fails SILENTLY in the worst possible direction — a
 * missing `sibling` makes the cross-record-type count return 0, which is exactly
 * the "no symptom at all" failure `HiddenCustomFieldsHint` exists to close.
 * `customFieldUtils.registry.test.ts` pins the invariants (every sibling is
 * itself registered, and siblings pair symmetrically).
 *
 * Every other entity's fields always apply to all; add a new one HERE.
 */
export const RELATIONSHIP_SCOPED_ENTITIES: Record<string, RelationshipScopedEntity> = {
  company: {
    labelGroup: 'companyRelationship',
    sibling: 'contact',
    otherCountKey: 'custom_fields:hiddenFields.alsoOnContacts',
    zeroCarrierElsewhereKey: 'custom_fields:dialog.zeroCarrierOnContacts',
  },
  contact: {
    labelGroup: 'contactRelationship',
    sibling: 'company',
    otherCountKey: 'custom_fields:hiddenFields.alsoOnCompanies',
    zeroCarrierElsewhereKey: 'custom_fields:dialog.zeroCarrierOnCompanies',
  },
};

/**
 * Label groups alone, DERIVED — so the scope picker and the definition badge
 * cannot disagree with the registry above about which entities are scoped.
 */
export const RELATIONSHIP_SCOPED_ENTITY_LABEL_GROUPS: Record<
  string,
  'companyRelationship' | 'contactRelationship'
> = Object.fromEntries(
  Object.entries(RELATIONSHIP_SCOPED_ENTITIES).map(([entityType, meta]) => [
    entityType,
    meta.labelGroup,
  ])
);

/**
 * Namespace prefix for tenant-defined relationship-class scope tokens (Phase 3).
 * A definition scoped to a tenant class stores `class:<id>` in
 * `applies_to_relationships`; an entity carrying that class contributes the same
 * token to its effective relationships. Mirrors the backend `CLASS_SCOPE_PREFIX`
 * so built-in enum values (bare) can never collide.
 */
export const CLASS_SCOPE_PREFIX = 'class:';

/** The namespaced scope identifier for a tenant class id. */
export function classScopeToken(classId: string): string {
  return `${CLASS_SCOPE_PREFIX}${classId}`;
}

/** True when a scope token refers to a tenant class (vs a built-in relationship). */
export function isClassScopeToken(token: string): boolean {
  return token.startsWith(CLASS_SCOPE_PREFIX);
}

/** Extract the class id from a `class:<id>` token (null if not a class token). */
export function classIdFromToken(token: string): string | null {
  return isClassScopeToken(token) ? token.slice(CLASS_SCOPE_PREFIX.length) : null;
}

/**
 * The effective relationship scope an entity carries: built-in relationships
 * plus a `class:<id>` token per tenant class it is tagged with. Hand this to
 * `CustomFieldsSection`/`useCustomFieldsAutoSave` so a field scoped to either a
 * built-in relationship OR a tenant class is inherited (union / OR semantics).
 * Mirrors the backend `relationship_scope_tokens`.
 */
export function relationshipScopeTokens(
  builtIn: string[] | undefined,
  classIds: string[] | undefined
): string[] {
  return [...(builtIn ?? []), ...(classIds ?? []).map(classScopeToken)];
}

/** Active definitions for an entity, ordered by display_order then key. */
export function activeCustomFieldDefinitions(
  definitions: CustomFieldDefinition[] | undefined
): CustomFieldDefinition[] {
  return (definitions ?? [])
    .filter((d) => d.is_active)
    .slice()
    .sort((a, b) => a.display_order - b.display_order || a.key.localeCompare(b.key));
}

/**
 * Filter definitions to those an entity INHERITS given its relationship classes
 * (Phase 2). Mirrors CustomFieldService.visible_definitions on the backend,
 * OR / any-of semantics:
 *
 * - An unscoped definition (empty `applies_to_relationships`) always shows.
 * - `relationships === undefined` → the entity doesn't participate in
 *   relationship scoping (deals, products, tickets…): scope is ignored, every
 *   definition shows (backward-compatible).
 * - Otherwise a definition shows iff its scope intersects the entity's
 *   relationships (the entity inherits the union of every class's field-set).
 */
export function visibleCustomFieldDefinitions(
  definitions: CustomFieldDefinition[],
  relationships: string[] | undefined
): CustomFieldDefinition[] {
  if (relationships === undefined) return definitions;
  const rels = new Set(relationships);
  return definitions.filter((d) => {
    const scope = d.applies_to_relationships ?? [];
    return scope.length === 0 || scope.some((r) => rels.has(r));
  });
}

/**
 * The complement of `visibleCustomFieldDefinitions`: active definitions this
 * entity does NOT inherit, because their scope names relationships it does not
 * carry.
 *
 * A field filtered out this way renders nowhere and says nothing, which is the
 * single most reported custom-field confusion — an admin defines a field, opens
 * a record that "has that relationship", and sees the older field alone. Handing
 * the hidden set to `HiddenCustomFieldsHint` turns that silence into a sentence
 * naming each missing field and the relationship it waits for.
 *
 * `relationships === undefined` (an entity that does not participate in
 * relationship scoping) hides nothing, so the set is empty.
 */
export function hiddenCustomFieldDefinitions(
  definitions: CustomFieldDefinition[],
  relationships: string[] | undefined
): CustomFieldDefinition[] {
  if (relationships === undefined) return [];
  const visible = new Set(visibleCustomFieldDefinitions(definitions, relationships));
  return definitions.filter((d) => !visible.has(d));
}

/**
 * A scope token's display label: the built-in relationship's i18n label, or the
 * tenant class's own (language-resolved) label.
 *
 * Spelled once because two surfaces render the same token — the admin page's
 * scope badge and the record page's hidden-fields hint — and a second copy is
 * how one of them starts calling an archived class "unknown" while the other
 * names it. `classesById` must be built from a list fetched with
 * `includeInactive: true`; an archived class a definition still carries is a
 * real answer, not an unknown one.
 */
export function resolveScopeTokenLabel(
  token: string,
  labelGroup: string,
  classesById: Map<string, RelationshipClass>,
  t: TFunction
): string {
  if (isClassScopeToken(token)) {
    const id = classIdFromToken(token);
    const cls = id ? classesById.get(id) : undefined;
    return cls
      ? resolveRelationshipClassLabel(cls, i18n.language)
      : t('custom_fields:relationshipClasses.unknownClass');
  }
  return t(`common:businessClassification.${labelGroup}.${token}`);
}

/**
 * How many ACTIVE definitions on the OTHER record type are scoped to a
 * relationship THIS record carries.
 *
 * A non-zero count is the answer to "I defined that field for this
 * relationship, why can't I see it?" when the record type is the reason — the
 * failure mode with no symptom at all, because a record whose own type defines
 * nothing renders no custom-fields card whatsoever.
 *
 * Takes the tenant's whole definition set (`useAllCustomFieldDefinitions`);
 * inactive rows are excluded, since they render on no record either way.
 */
export function otherRecordTypeFieldCount(
  allDefinitions: CustomFieldDefinition[] | undefined,
  entityType: string,
  relationships: string[] | undefined
): number {
  const sibling = RELATIONSHIP_SCOPED_ENTITIES[entityType]?.sibling;
  if (!sibling || relationships === undefined) return 0;
  const carried = new Set(relationships);
  return (allDefinitions ?? []).filter(
    (d) =>
      d.is_active &&
      d.entity_type === sibling &&
      (d.applies_to_relationships ?? []).some((token) => carried.has(token))
  ).length;
}

/**
 * Which record types actually define a field for each relationship scope token.
 *
 * A relationship — built-in or a tenant class — is GLOBAL across companies and
 * contacts, but a custom field definition belongs to exactly ONE `entity_type`.
 * That asymmetry is the whole trap the admin page exists to explain: scoping an
 * Organization field to "Interior Designer" and then tagging a CONTACT with the
 * same relationship shows nothing, correctly and silently. Feeding this map to
 * the relationship list turns that into a visible fact ("Organizations" / "no
 * fields yet") instead of a support ticket.
 *
 * Takes the tenant's whole definition set (`useAllCustomFieldDefinitions`) —
 * each row carries its own `entity_type`, so no per-record-type fan-out and no
 * admin-gated entity list is needed to compute this. Inactive definitions are
 * excluded: they render on no record, so counting them would promise a field the
 * user cannot see. Values are record-type keys in first-seen order; the caller
 * resolves them to labels.
 */
export function relationshipScopeUsage(
  definitions: CustomFieldDefinition[] | undefined
): Map<string, string[]> {
  const usage = new Map<string, string[]>();
  for (const definition of definitions ?? []) {
    if (!definition.is_active) continue;
    for (const token of definition.applies_to_relationships ?? []) {
      const entities = usage.get(token);
      if (!entities) usage.set(token, [definition.entity_type]);
      else if (!entities.includes(definition.entity_type)) entities.push(definition.entity_type);
    }
  }
  return usage;
}

/** A selected class that no record of the definition's OWN record type carries. */
export interface ZeroCarrierScope {
  token: string;
  classId: string;
  /** Records of the OTHER relationship-bearing type that DO carry it (may be 0). */
  siblingCount: number;
  /** That other record type's registry key — `company` or `contact`. */
  siblingEntityType: string;
}

/**
 * Selected class scopes that no record of `entityType` carries — i.e. the field
 * being defined would render on nothing, anywhere.
 *
 * A relationship class is tenant-GLOBAL (organizations AND contacts share the
 * vocabulary) while a definition belongs to exactly ONE `entity_type`. Nothing in
 * the picker showed that asymmetry, so a field could be created into permanent
 * invisibility with no feedback at any point — which is what happened to the
 * reporting tenant's `company :: profession`, scoped to a class 713 contacts and
 * zero organizations carried (WI f130d85c).
 *
 * `siblingCount` is what turns "this is empty" into a diagnosis: a class the
 * OTHER record type carries in numbers says the field is on the wrong record
 * type, while zero on both sides says nobody has been tagged yet. Built-in
 * relationship values are not judged — the picker already labels those per record
 * type, and the counts endpoint is about classes.
 *
 * Returns [] while the counts are still loading, so a warning never flashes on a
 * scope that turns out to be carried.
 */
export function zeroCarrierScopes(
  entityType: string,
  selectedTokens: readonly string[],
  carrierCounts: RelationshipClassCarrierCount[] | undefined
): ZeroCarrierScope[] {
  const scoped = RELATIONSHIP_SCOPED_ENTITIES[entityType];
  if (!scoped || !carrierCounts) return [];
  const byToken = new Map(carrierCounts.map((c) => [c.scope_token, c]));
  const ownCount = (c: RelationshipClassCarrierCount) =>
    entityType === 'company' ? c.organization_count : c.contact_count;
  const siblingCount = (c: RelationshipClassCarrierCount) =>
    entityType === 'company' ? c.contact_count : c.organization_count;

  const result: ZeroCarrierScope[] = [];
  for (const token of selectedTokens) {
    const classId = classIdFromToken(token);
    if (!classId) continue;
    const counts = byToken.get(token);
    // A token the server did not count is a class that no longer exists — the
    // hint surfaces that as "Unknown custom relationship" already, and inventing
    // a carrier warning for it would name a class nobody can act on.
    if (!counts || ownCount(counts) > 0) continue;
    result.push({
      token,
      classId,
      siblingCount: siblingCount(counts),
      siblingEntityType: scoped.sibling,
    });
  }
  return result;
}

/**
 * Active definitions whose ONLY scope is this class — deleting it leaves them
 * unscoped, i.e. shown on EVERY record of their type.
 *
 * The delete strips the token in the same transaction now, which is the right
 * repair for a dangling reference and the wrong surprise for an admin: the field
 * widens rather than disappearing. So the confirmation says so first.
 */
export function definitionsLosingTheirOnlyScope(
  definitions: CustomFieldDefinition[] | undefined,
  scopeToken: string
): CustomFieldDefinition[] {
  return (definitions ?? []).filter(
    (d) =>
      d.is_active &&
      (d.applies_to_relationships ?? []).length === 1 &&
      d.applies_to_relationships?.[0] === scopeToken
  );
}

/** Resolve a definition's label for the active language, falling back to `label`. */
export function resolveCustomFieldLabel(definition: CustomFieldDefinition): string {
  return resolveTranslated(definition.label, definition.label_translations);
}

/** Resolve a select option's label for the active language, falling back to `label`. */
export function resolveCustomFieldOptionLabel(option: CustomFieldOption): string {
  return resolveTranslated(option.label, option.label_translations);
}

function resolveTranslated(
  fallback: string,
  translations: Record<string, string> | null | undefined
): string {
  if (!translations) return fallback;
  const lang = (i18n.language || 'en').split('-')[0];
  return translations[lang] || translations[i18n.language] || fallback;
}

/** A value the user has not supplied (mirrors the backend's `_is_empty`). */
export function isCustomFieldEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

/**
 * Validate a full custom_fields payload against the entity's active definitions.
 * Mirrors CustomFieldService.validate (partial=False): required fields plus the
 * per-type constraints. Returns errors keyed by the definition `key`.
 */
export function validateCustomFields(
  definitions: CustomFieldDefinition[],
  values: Record<string, unknown>,
  t: TFunction
): CustomFieldErrors {
  const errors: CustomFieldErrors = {};

  for (const def of definitions) {
    const value = values[def.key];
    const validation = (def.validation as Record<string, unknown>) || {};

    if (isCustomFieldEmpty(value)) {
      if (def.required) {
        errors[def.key] = t('custom_fields:validation.required');
      }
      continue; // empty optional values skip type checks (matches the server)
    }

    switch (def.field_type) {
      case 'text': {
        if (typeof value !== 'string') break;
        if (typeof validation.min_length === 'number' && value.length < validation.min_length) {
          errors[def.key] = t('custom_fields:validation.minLength', { count: validation.min_length });
        } else if (typeof validation.max_length === 'number' && value.length > validation.max_length) {
          errors[def.key] = t('custom_fields:validation.maxLength', { count: validation.max_length });
        } else if (typeof validation.pattern === 'string' && validation.pattern) {
          let ok = true;
          try {
            ok = new RegExp(validation.pattern).test(value);
          } catch {
            ok = true; // a malformed pattern is rejected at definition-save time
          }
          if (!ok) errors[def.key] = t('custom_fields:validation.pattern');
        }
        break;
      }
      case 'number':
      case 'currency': {
        if (typeof value !== 'number' || Number.isNaN(value)) {
          errors[def.key] = t('custom_fields:validation.number');
        } else if (typeof validation.min === 'number' && value < validation.min) {
          errors[def.key] = t('custom_fields:validation.min', { min: validation.min });
        } else if (typeof validation.max === 'number' && value > validation.max) {
          errors[def.key] = t('custom_fields:validation.max', { max: validation.max });
        }
        break;
      }
      case 'multi_select': {
        if (Array.isArray(value) && typeof validation.max_items === 'number' && value.length > validation.max_items) {
          errors[def.key] = t('custom_fields:validation.maxItems', { count: validation.max_items });
        }
        break;
      }
      // boolean / date / select have no extra client constraints beyond required;
      // the server enforces date range and option membership authoritatively.
      default:
        break;
    }
  }

  return errors;
}
